class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    if (facetForm) {
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
      facetForm.addEventListener('change', (event) => {
        if (event.target.type !== 'checkbox' || event.target.classList.contains('facets-merge-sync')) return;
        const primaryId = event.target.id;
        if (!primaryId) return;
        facetForm
          .querySelectorAll(`.facets-merge-sync[data-merge-primary="${primaryId}"]`)
          .forEach((hidden) => {
            hidden.checked = event.target.checked;
          });
      });
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static TAG_PARAM = 'filter.p.tag';

  static TAG_MERGE_PAIRS = [
    ['type-deck', 'type-deck set'],
    ['type-promo', 'type-promo card'],
  ];

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static getTagFilterOperator() {
    const form =
      document.querySelector('#FacetFiltersForm[data-tag-filter-operator]') ||
      document.querySelector('#FacetFiltersFormMobile[data-tag-filter-operator]');
    return form?.dataset.tagFilterOperator || 'OR';
  }

  static cartesian(groups) {
    return groups.reduce(
      (acc, group) => acc.flatMap((prefix) => group.map((item) => [...prefix, item])),
      [[]]
    );
  }

  static beginRequest() {
    FacetFiltersForm.latestRequestId += 1;
    if (FacetFiltersForm.activeAbortController) {
      FacetFiltersForm.activeAbortController.abort();
    }
    FacetFiltersForm.activeAbortController = new AbortController();
    return {
      requestId: FacetFiltersForm.latestRequestId,
      signal: FacetFiltersForm.activeAbortController.signal,
    };
  }

  static isLatestRequest(requestId) {
    return requestId === FacetFiltersForm.latestRequestId;
  }

  static expandSearchParamsForAndTagMerge(searchParams) {
    if (FacetFiltersForm.getTagFilterOperator() !== 'AND') return null;

    const params = new URLSearchParams(searchParams);
    const tags = params.getAll(FacetFiltersForm.TAG_PARAM);
    if (!tags.length) return null;

    const tagSet = new Set(tags);
    const hasActiveMergePair = FacetFiltersForm.TAG_MERGE_PAIRS.some(
      ([primary, secondary]) => tagSet.has(primary) && tagSet.has(secondary)
    );
    if (!hasActiveMergePair) return null;

    const consumed = new Set();
    const typeOrGroups = [];

    FacetFiltersForm.TAG_MERGE_PAIRS.forEach(([primary, secondary]) => {
      if (tagSet.has(primary) && tagSet.has(secondary)) {
        typeOrGroups.push([primary, secondary]);
        consumed.add(primary);
        consumed.add(secondary);
      }
    });

    const brandTags = [];
    const otherTags = [];

    tags.forEach((tag) => {
      if (consumed.has(tag)) return;
      const lower = tag.toLowerCase();
      if (lower.startsWith('brand-')) brandTags.push(tag);
      else if (lower.startsWith('type-')) typeOrGroups.push([tag]);
      else otherTags.push(tag);
    });

    const combinations = FacetFiltersForm.cartesian(typeOrGroups);
    return combinations.map((combo) => {
      const expandedParams = new URLSearchParams(searchParams);
      expandedParams.delete(FacetFiltersForm.TAG_PARAM);
      [...otherTags, ...brandTags, ...combo].forEach((tag) => {
        expandedParams.append(FacetFiltersForm.TAG_PARAM, tag);
      });
      return expandedParams.toString();
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    const expandedSearchParams = FacetFiltersForm.expandSearchParamsForAndTagMerge(searchParams);
    if (expandedSearchParams && expandedSearchParams.length > 1) {
      FacetFiltersForm.renderPageMerged(searchParams, expandedSearchParams, event, updateURLHash);
      return;
    }

    FacetFiltersForm.searchParamsPrev = searchParams;
    const { requestId, signal } = FacetFiltersForm.beginRequest();
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, requestId)
        : FacetFiltersForm.renderSectionFromFetch(url, event, requestId, signal);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderPageMerged(canonicalSearchParams, expandedSearchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = canonicalSearchParams;
    const { requestId, signal } = FacetFiltersForm.beginRequest();
    const sections = FacetFiltersForm.getSections();
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');

    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    if (countContainer) countContainer.classList.add('loading');
    if (countContainerDesktop) countContainerDesktop.classList.add('loading');

    Promise.all(
      sections.map((section) => {
        const canonicalUrl = `${window.location.pathname}?section_id=${section.section}&${canonicalSearchParams}`;
        return Promise.all([
          fetch(canonicalUrl, { signal }).then((response) => response.text()),
          ...expandedSearchParams.map((searchParams) => {
            const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
            return fetch(url, { signal }).then((response) => response.text());
          }),
        ]);
      })
    )
      .then((sectionResponses) => {
        if (!FacetFiltersForm.isLatestRequest(requestId)) return;

        const [canonicalText, ...gridTexts] = sectionResponses[0];
        const parsedResponses = gridTexts.map((responseText) =>
          new DOMParser().parseFromString(responseText, 'text/html')
        );

        const mergedFiltersHtml = FacetFiltersForm.mergeFilterCounts(canonicalText, parsedResponses);
        FacetFiltersForm.renderFilters(mergedFiltersHtml, event);
        FacetFiltersForm.renderMergedProductGrid(parsedResponses);
        FacetFiltersForm.renderMergedProductCount(parsedResponses);
        if (typeof initializeScrollAnimationTrigger === 'function') {
          const gridRoot = document.getElementById('ProductGridContainer') || document;
          initializeScrollAnimationTrigger(gridRoot);
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        throw error;
      })
      .finally(() => {
        if (!FacetFiltersForm.isLatestRequest(requestId)) return;
        loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
      });

    if (updateURLHash) FacetFiltersForm.updateURLHash(canonicalSearchParams);
  }

  static syncFilterItemDisabledState(listItem, count) {
    if (!listItem) return;

    const input = listItem.querySelector(
      'input[type="checkbox"][name]:not(.facets-merge-sync)'
    );
    if (!input || input.name !== FacetFiltersForm.TAG_PARAM) return;

    const shouldDisable = count === 0 && !input.checked;
    input.disabled = shouldDisable;

    const label = listItem.querySelector(
      'label.facet-checkbox, label.mobile-facets__label, .facets__label.facet-checkbox, .facets__label.mobile-facets__label'
    );
    if (label) {
      label.classList.toggle('disabled', shouldDisable);
    }

    const swatchInput = listItem.querySelector('.swatch-input__input, .swatch-input input[type="checkbox"]');
    if (swatchInput) {
      swatchInput.disabled = shouldDisable;
    }
  }

  static syncAllFilterDisabledStates(root = document) {
    root.querySelectorAll('[data-filter-count]').forEach((countSpan) => {
      const listItem = countSpan.closest('li');
      const count = parseInt(countSpan.textContent ?? '0', 10) || 0;
      FacetFiltersForm.syncFilterItemDisabledState(listItem, count);
    });
  }

  static mergeFilterCounts(canonicalText, parsedResponses) {
    // Display-only count correction for merged tag filters.
    // Assumes expanded responses do not duplicate the same product across branches.
    // Scope: filter.p.tag only; fail-safe keeps canonical counts when input is missing.
    const canonicalDOM = new DOMParser().parseFromString(canonicalText, 'text/html');

    canonicalDOM.querySelectorAll('[data-filter-count]').forEach((countSpan) => {
      const input = countSpan.closest('li')?.querySelector('input[type="checkbox"][name]');
      if (!input) return;

      const { name, value } = input;
      if (name !== FacetFiltersForm.TAG_PARAM) return;

      let total = 0;

      parsedResponses.forEach((parsedResponse) => {
        const matching = Array.from(
          parsedResponse.querySelectorAll(`input[type="checkbox"][name="${name}"]`)
        ).find((el) => el.value === value);
        const matchingCount = matching?.closest('li')?.querySelector('[data-filter-count]');
        total += parseInt(matchingCount?.textContent ?? '0', 10) || 0;
      });

      if (Number.isFinite(total) && total >= 0) {
        countSpan.textContent = String(total);
        const sr = countSpan.closest('li')?.querySelector('[data-filter-count-sr]');
        if (sr) sr.textContent = sr.textContent.replace(/\d+/, String(total));
        FacetFiltersForm.syncFilterItemDisabledState(countSpan.closest('li'), total);
      }
    });

    return canonicalDOM.documentElement.outerHTML;
  }

  static getProductCards(documentFragment) {
    return Array.from(
      documentFragment.querySelectorAll('#ProductGridContainer .grid__item, #ProductGridContainer li.grid__item')
    );
  }

  static getProductKey(card) {
    return (
      card.querySelector('[data-product-id]')?.dataset.productId ||
      card.querySelector('a[href*="/products/"]')?.getAttribute('href') ||
      card.innerHTML
    );
  }

  static renderMergedProductGrid(parsedResponses) {
    const mergedCards = [];
    const seenKeys = new Set();

    parsedResponses.forEach((parsedHTML) => {
      FacetFiltersForm.getProductCards(parsedHTML).forEach((card) => {
        const key = FacetFiltersForm.getProductKey(card);
        if (seenKeys.has(key)) return;
        seenKeys.add(key);
        mergedCards.push(card.cloneNode(true));
      });
    });

    const gridContainer = document.getElementById('ProductGridContainer');
    const templateResponse =
      parsedResponses.find((parsedHTML) => FacetFiltersForm.getProductCards(parsedHTML).length > 0) ||
      parsedResponses[0];
    const fetchedContainer = templateResponse.getElementById('ProductGridContainer');

    gridContainer.innerHTML = fetchedContainer.innerHTML;

    if (!mergedCards.length) {
      gridContainer.querySelector('.collection')?.classList.remove('loading');
      return;
    }

    const productGrid =
      gridContainer.querySelector('#product-grid.grid') ||
      gridContainer.querySelector('ul#product-grid.product-grid');

    if (!productGrid) {
      gridContainer.querySelector('.collection')?.classList.remove('loading');
      return;
    }

    productGrid.innerHTML = '';
    mergedCards.forEach((card) => productGrid.appendChild(card));

    gridContainer.querySelector('.collection')?.classList.remove('loading');
    gridContainer
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => element.classList.add('scroll-trigger--cancel'));
  }

  static renderMergedProductCount(parsedResponses) {
    const seenKeys = new Set();
    let mergedCount = 0;

    parsedResponses.forEach((parsedHTML) => {
      FacetFiltersForm.getProductCards(parsedHTML).forEach((card) => {
        const key = FacetFiltersForm.getProductKey(card);
        if (seenKeys.has(key)) return;
        seenKeys.add(key);
        mergedCount += 1;
      });
    });

    const primaryCount = parsedResponses[0].getElementById('ProductCount');
    const primaryCountDesktop = parsedResponses[0].getElementById('ProductCountDesktop');
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');

    if (container && primaryCount) {
      container.innerHTML = primaryCount.innerHTML.replace(/\d+/g, String(mergedCount));
      container.classList.remove('loading');
    }
    if (containerDesktop && primaryCountDesktop) {
      containerDesktop.innerHTML = primaryCountDesktop.innerHTML.replace(/\d+/g, String(mergedCount));
      containerDesktop.classList.remove('loading');
    }
  }

  static renderSectionFromFetch(url, event, requestId, signal) {
    fetch(url, { signal })
      .then((response) => response.text())
      .then((responseText) => {
        if (!FacetFiltersForm.isLatestRequest(requestId)) return;

        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(document.getElementById('ProductGridContainer') || document);
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        throw error;
      });
  }

  static renderSectionFromCache(filterDataUrl, event, requestId) {
    if (!FacetFiltersForm.isLatestRequest(requestId)) return;

    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger(document.getElementById('ProductGridContainer') || document);
    }
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender && event?.target) {
      const closestJSFilterID = event.target.closest('.js-filter')?.id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }

    FacetFiltersForm.syncAllFilterDisabledStates();
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const link = event.currentTarget;
    let searchParams =
      link.href.indexOf('?') == -1
        ? ''
        : link.href.slice(link.href.indexOf('?') + 1);

    if (
      link.classList.contains('merged-active-facet') &&
      link.dataset.hasSecondaryActive === 'true' &&
      link.dataset.secondaryParam &&
      link.dataset.secondaryValue
    ) {
      const params = new URLSearchParams(searchParams);
      const param = link.dataset.secondaryParam;
      const secondaryValue = link.dataset.secondaryValue;
      const remainingValues = params.getAll(param).filter((value) => value !== secondaryValue);
      params.delete(param);
      remainingValues.forEach((value) => params.append(param, value));
      searchParams = params.toString();
    }

    FacetFiltersForm.renderPage(searchParams);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.latestRequestId = 0;
FacetFiltersForm.activeAbortController = null;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

document.addEventListener('DOMContentLoaded', () => {
  const initialSearchParams = FacetFiltersForm.searchParamsInitial;
  if (!initialSearchParams) return;

  const expandedSearchParams = FacetFiltersForm.expandSearchParamsForAndTagMerge(initialSearchParams);
  if (expandedSearchParams && expandedSearchParams.length > 1) {
    FacetFiltersForm.renderPage(initialSearchParams, null, false);
  }
});

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) minInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') minInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
